import React, { useState } from "react";
import { useAppContext } from "../../../contexts/AppContext";
import Dialog from "carbon-react/lib/components/dialog";
import Heading from "carbon-react/lib/components/heading";
import Content from "carbon-react/lib/components/content";
import TextArea from "carbon-react/lib/__experimental__/components/textarea";
import ErrorMessage from "../../messages/ErrorMessage";
import LanguageSelector from "../../common/languageSelector/LanguageSelector";
import DialogConfirmButtons from "../../common/dialogConfirmButtons/DialogConfirmButtons";
import ConfirmDialog from "../../common/confirmDialog/ConfirmDialog";
import { useUserApi } from "../../../services/UserApi";
import StepTitle from "../../common/stepTitle/StepTitle";
import StepHeading from "../../common/stepHeading/StepHeading";

// import "./CommentsDialog.css";

const initialState = {
  languageCode: null,
  validLanguageCode: true,
  agentComments: "",
  validAgentComments: true,
};

const CommentsDialog = ({ open, user, onCancel, onDeleted }) => {
  const { deleteUser } = useUserApi();
  const [errorMsg, setErrorMsg] = useState("");
  const [showError, setShowError] = useState(false);
  const [languageCode, setLanguageCode] = useState(initialState.languageCode);
  const [validLanguageCode, setValidLanguageCode] = useState(
    initialState.validLanguageCode
  );
  const [agentComments, setAgentComments] = useState(
    initialState.agentComments
  );
  const [validAgentComments, setValidAgentComments] = useState(
    initialState.validAgentComments
  );
  const [busy, setBusy] = useState(false);
  const { toastSuccess } = useAppContext();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const showDialogError = (msg) => {
    setErrorMsg(msg);
    setShowError(true);
  };

  const clearDialogError = () => {
    setErrorMsg("");
    setShowError(false);
  };

  const validateAgentComments = () => {
    let valid = true;

    if (agentComments.length === 0) {
      valid = false;
    }

    setValidAgentComments(valid);
    return valid;
  };

  const validateLanguageCode = () => {
    let valid = languageCode && languageCode.length > 0;
    setValidLanguageCode(valid);
    return valid;
  };

  const validateInputs = () => {
    let valid = true;
    if (!validateLanguageCode()) valid = false;
    if (!validateAgentComments()) valid = false;
    return valid;
  };

  const onDialogDelete = () => {
    if (!validateInputs()) return;
    setConfirmDeleteOpen(true);
  };

  const onConfirmDeleteCancel = () => {
    setConfirmDeleteOpen(false);
  };

  const onConfirmDeleteContinue = async () => {
    setConfirmDeleteOpen(false);
    await performUserDelete();
  };

  const performUserDelete = async () => {
    if (busy) return;

    try {
      clearDialogError();
      setBusy(true);

      var apiResult = await deleteUser(
        user.userId,
        languageCode,
        agentComments
      );

      if (!apiResult?.success)
        throw new Error(
          apiResult?.error?.description || "The delete operation failed"
        );

      toastSuccess(`User successfully deleted - '${user.email}'`);
      resetDialogState();
      onDeleted();
    } catch (error) {
      showDialogError(error.toString());
      setConfirmDeleteOpen(false);
    }
    setBusy(false);
  };

  const resetDialogState = () => {
    clearDialogError();
    setConfirmDeleteOpen(false);
    setLanguageCode(initialState.languageCode);
    setValidLanguageCode(initialState.validLanguageCode);
    setAgentComments(initialState.agentComments);
    setValidAgentComments(initialState.validAgentComments);
  };

  const onDialogCancel = () => {
    resetDialogState();
    onCancel();
  };

  const agentCommentsValidationAttributes = {
    hasWarning: true,
    inputIcon: "warning",
    tooltipMessage: "Please indicate why this action is being performed.",
  };

  const languageSelection = (
    <LanguageSelector
      required={false}
      autoFocus={true}
      appearBusy={busy}
      value={languageCode}
      onValueChanged={(newLanguage) => {
        setLanguageCode(newLanguage);
      }}
      onBlur={validateLanguageCode}
      appearInvalid={!validLanguageCode}
      validationMsg="Language must be provided"
    />
  );

  const confirmDeletion = (
    <ConfirmDialog
      open={confirmDeleteOpen}
      prompt="This action is irreversible, are you certain you want to delete this user?"
      confirmBtnLabel="Delete"
      onConfirm={onConfirmDeleteContinue}
      onCancel={onConfirmDeleteCancel}
    />
  );

  const agentCommentsInput = (
    <Content title="Comments">
      <TextArea
        name="delete-user-agent-comments"
        disabled={busy}
        value={agentComments}
        characterLimit={"250"}
        enforceCharacterLimit={true}
        placeholder={"Please indicate why this action is being performed."}
        onChange={(e) => {
          setValidAgentComments(true);
          setAgentComments(e.target.value);
        }}
        onBlur={validateAgentComments}
        {...(!validAgentComments ? agentCommentsValidationAttributes : {})}
      />
    </Content>
  );

  const dialogButtons = (
    <DialogConfirmButtons
      appearBusy={busy}
      confirmBtnLabel={"Delete user"}
      onConfirm={onDialogDelete}
      onCancel={onDialogCancel}
    />
  );

  const errorMessage = (
    <>
      {showError !== true ? null : (
        <ErrorMessage
          message={
            errorMsg && errorMsg.length > 0 ? errorMsg : "The operation failed!"
          }
        />
      )}
    </>
  );

  return (
    <>
      <Dialog open={open} showCloseIcon={true} onCancel={onDialogCancel}>
        <Heading title="Delete user" />
        <StepTitle title="Deleting a user will impact ALL applications secured via this identity." />
        <StepHeading label="Email address" value={user?.email} />
        <Content>
          This feature will permanently delete the user identity, thereby
          preventing the user from accessing all associated products and
          services.
        </Content>
        <StepTitle
          className="reset-password-disclaimer"
          title="This action is IRREVERSIBLE.  Once deleted, a user identity CANNOT be recovered."
        />
        {languageSelection}
        {agentCommentsInput}
        {dialogButtons}
        {errorMessage}
      </Dialog>
      {confirmDeletion}
    </>
  );
};

export default CommentsDialog;
